import { CurrencyInput, FormGroup } from '@starsoft/common/components';
import { useFormContext } from 'react-hook-form';
import styles from './styles.module.scss';
import { CoinType } from '@starsoft/common/models';
import { CreateDepositRequestDto } from '@starsoft/common/dto';
import { useTranslation } from '../../../../../../hooks';
import useDepositSearchParams from '../../../../../../hooks/deposit/useDepositSearchParams';
import { useDepositForm } from '../../../../../../hooks/deposit';
import { getMoneyOrAmount } from '@starsoft/common/utils';

/**
 * AmountInput Component
 * This component is responsible for rendering an input field for entering deposit amounts.
 * It handles form state and validation for deposit amounts, and displays error messages if the input is invalid.
 *
 * @returns {JSX.Element | null} The rendered component displaying the amount input field or null if the coin type is crypto.
 *
 * @example
 * // Usage example:
 * <AmountInput />
 */
export default function AmountInput() {
  // Translation hooks for different namespaces
  const { t } = useTranslation('deposit');
  const { t: tWithdraw } = useTranslation('withdraw');
  const { t: tNotify } = useTranslation('notify');
  // Retrieve the coin type from deposit search parameters
  const { coinType } = useDepositSearchParams();

  // Form context to manage form state and values
  const {
    setValue,
    watch,
    formState: { dirtyFields, touchedFields },
  } = useFormContext<CreateDepositRequestDto>();

  // Custom hook to manage deposit form state and queries
  const {
    isOverValued,
    isFormLoading,
    isUnderValued,
    maxValueFormatted,
    minValueFormatted,
    isValidReceiveAmount,
    accountByCoinQuery: { currentAccount, isLoadingAccountByCoin },
    coinMapping,
  } = useDepositForm(watch, setValue);

  // Determine if the form is in a loading state
  const isLoading: boolean = isLoadingAccountByCoin || isFormLoading;
  // Watch the 'amount' field in the form
  const amount = watch('amount');

  /**
   * Handles changes to the amount input field.
   * Updates the form value for the 'amount' field.
   *
   * @param {number} value - The new value for the amount.
   *
   * @example
   * handleChange(100);
   */
  function handleChange(value: number) {
    setValue('amount', value, {
      shouldDirty: true,
    });
  }

  // Return null if the coin type is crypto, as no amount input is needed
  if (coinType == CoinType.Crypto) {
    return null;
  }

  // Render the form group and currency input for deposit amount
  return (
    <FormGroup
      label={t('deposit_placeholder')}
      className={styles.wrapper}
      isLoading={isFormLoading}
      loading={{ label: true, secondary: false }}
    >
      <CurrencyInput
        hideCurrency
        value={amount as string}
        coin={currentAccount?.coin}
        setValue={handleChange}
        placeholder={t('deposit_placeholder')}
        hideLock
        isLoading={isLoading}
        isContainerColor
        registerOptions={{
          required: true,
          min: getMoneyOrAmount(coinMapping?.minimum),
          max: coinMapping?.maximum
            ? getMoneyOrAmount(coinMapping?.maximum)
            : undefined,
        }}
        isError={
          (!isValidReceiveAmount || isOverValued || isUnderValued) &&
          dirtyFields?.amount
        }
        isExternalTouched={!!touchedFields?.amount}
        errorMessage={
          isOverValued || isUnderValued
            ? tNotify('deposit_amount_error', {
                minAmount: minValueFormatted,
                maxAmount: maxValueFormatted,
              })
            : (tWithdraw('receive_amount_label', {
                minAmount: minValueFormatted,
                maxAmount: maxValueFormatted,
              }) as string)
        }
      />
    </FormGroup>
  );
}
